import getWinner from "../../../common/getWinner.ts";
import type {
	AllStarPlayer,
	Conditions,
	CustomTournament,
	CustomTournamentGame,
	CustomTournamentSelectionSettings,
	Player,
	ScheduleGameWithoutKey,
} from "../../../common/types.ts";
import { idb } from "../../db/index.ts";
import { g, helpers, logEvent, toUI } from "../../util/index.ts";
import { FormulaEvaluator } from "../../util/FormulaEvaluator.ts";

export const CUSTOM_TID_BASE = -1000;
const GAME_ATTRIBUTE_KEY = "customTournament";
const DEFAULT_FORMULA = "ovr + value / 10";

export type CustomTournamentInput = {
	name: string;
	type: "olympics" | "tournament" | "allStar";
	selectionSettings: CustomTournamentSelectionSettings;
	recurrence: "once" | "everySeason" | "everyNSeasons";
	recurrenceInterval?: number;
};

type TournamentTrigger =
	| "afterPreseason"
	| "beforePlayoffs"
	| "betweenPlayoffRounds"
	| "beforeDraft";

type RankedPlayer = {
	player: Player;
	country: string;
	score: number;
	position: string;
};

const getAttribute = async () => {
	const attribute = await idb.cache.gameAttributes.get(GAME_ATTRIBUTE_KEY);
	return attribute?.value as CustomTournament | undefined;
};

const save = async (tournament: CustomTournament) => {
	await idb.cache.gameAttributes.put({
		key: GAME_ATTRIBUTE_KEY,
		value: tournament,
	});
};

export const customTid = (teamID: number) => CUSTOM_TID_BASE - teamID;

export const customTeamID = (tid: number) => {
	if (tid > CUSTOM_TID_BASE) {
		return undefined;
	}

	return CUSTOM_TID_BASE - tid;
};

export const isCustomTid = (tid: number) => customTeamID(tid) !== undefined;

export const get = getAttribute;

export const getTeamByTid = async (tid: number) => {
	const teamID = customTeamID(tid);
	if (teamID === undefined) {
		return undefined;
	}

	const tournament = await getAttribute();
	if (!tournament || tournament.season !== g.get("season")) {
		return undefined;
	}

	return tournament.teams[teamID];
};

const getActivePlayers = () =>
	idb.cache.players.indexGetAll("playersByTid", [0, Infinity]);

const getFormulaEvaluator = (formula: string) =>
	new FormulaEvaluator(
		formula,
		[
			"ovr",
			"pot",
			"value",
			"age",
			"games",
			"experience",
			"injury",
			"ratings",
			"stats",
		] as const,
		["ratings", "stats"] as const,
	);

const rankPlayers = (
	players: Player[],
	settings: CustomTournamentSelectionSettings,
) => {
	const formula = settings.selectionFormula.trim() || DEFAULT_FORMULA;
	const evaluator = getFormulaEvaluator(formula);

	return players
		.map((player) => {
			const ratings = player.ratings.at(-1)!;
			const stats = player.stats.findLast(
				(stats) => stats.season === g.get("season") && !stats.playoffs,
			) ?? { gp: 0 };
			const numericRatings = Object.fromEntries(
				Object.entries(ratings).filter(
					([, value]) => typeof value === "number",
				),
			) as Record<string, number>;
			const numericStats = Object.fromEntries(
				Object.entries(stats).filter(([, value]) => typeof value === "number"),
			) as Record<string, number>;
			const score = evaluator.evaluate({
				ovr: ratings.ovr,
				pot: ratings.pot,
				value: player.valueNoPot,
				age: g.get("season") - player.born.year,
				games: stats.gp ?? 0,
				experience: player.stats.filter((row) => !row.playoffs).length,
				injury: player.injury.gamesRemaining > 0 ? 1 : 0,
				ratings: numericRatings,
				stats: numericStats,
			});

			return {
				player,
				country: helpers.getCountry(player.born.loc),
				score: Number.isFinite(score) ? score : -Infinity,
				position: ratings.pos,
			};
		})
		.sort((a, b) => b.score - a.score || b.player.value - a.player.value);
};

const selectRoster = (
	players: RankedPlayer[],
	settings: CustomTournamentSelectionSettings,
) => {
	if (!settings.balancePositions) {
		return players.slice(0, settings.rosterSize);
	}

	const selected: RankedPlayer[] = [];
	const remaining = [...players];
	const positions = [...new Set(players.map((player) => player.position))];
	while (selected.length < settings.rosterSize && remaining.length > 0) {
		const preferredPosition = positions[selected.length % positions.length];
		const index = remaining.findIndex(
			(player) => player.position === preferredPosition,
		);
		selected.push(remaining.splice(index === -1 ? 0 : index, 1)[0]!);
	}
	return selected;
};

const playerToInfo = (player: Player): AllStarPlayer => ({
	pid: player.pid,
	tid: player.tid,
	name: `${player.firstName} ${player.lastName}`,
	...(player.injury.gamesRemaining > 0 ? { injured: true } : {}),
});

const buildOlympicTeams = async (
	settings: CustomTournamentSelectionSettings,
) => {
	const activePlayers = await getActivePlayers();
	const eligiblePlayers = activePlayers.filter(
		(player) => settings.includeInjured || player.injury.gamesRemaining === 0,
	);
	const ranked = rankPlayers(eligiblePlayers, settings);
	const byCountry = Map.groupBy(
		ranked.filter((player) => player.country !== "None"),
		(player) => player.country,
	);
	const requestedCountries = settings.countries?.filter((country) =>
		byCountry.has(country),
	);
	const countries = (
		requestedCountries?.length
			? requestedCountries
			: [...byCountry.entries()]
					.filter(
						([, players]) => players.length >= settings.minPlayersPerCountry,
					)
					.sort(
						(a, b) =>
							b[1]
								.slice(0, settings.rosterSize)
								.reduce((sum, player) => sum + player.score, 0) -
							a[1]
								.slice(0, settings.rosterSize)
								.reduce((sum, player) => sum + player.score, 0),
					)
					.map(([country]) => country)
	).slice(0, settings.numCountries);

	if (countries.length < 2) {
		throw new Error("Not enough countries have enough eligible players");
	}

	return countries.map((country, id) => {
		const roster = selectRoster(byCountry.get(country) ?? [], settings);
		if (roster.length < settings.rosterSize) {
			throw new Error(
				`${country} only has ${roster.length} eligible players; lower the roster size or change the eligibility rules`,
			);
		}
		return {
			id,
			name: country,
			players: roster.map(({ player }) => playerToInfo(player)),
		};
	});
};

const buildAllStarTeams = async (
	settings: CustomTournamentSelectionSettings,
) => {
	const activePlayers = await getActivePlayers();
	const eligiblePlayers = activePlayers.filter(
		(player) => settings.includeInjured || player.injury.gamesRemaining === 0,
	);
	const ranked = selectRoster(rankPlayers(eligiblePlayers, settings), {
		...settings,
		rosterSize: settings.rosterSize * 2,
	});
	return [0, 1].map((id) => ({
		id,
		name: `All-Stars ${id + 1}`,
		players: ranked
			.filter((_, index) => index % 2 === id)
			.map(({ player }) => playerToInfo(player)),
	}));
};

const buildLeagueTeams = async (
	settings: CustomTournamentSelectionSettings,
) => {
	const teams = (await idb.cache.teams.getAll()).filter(
		(team) => !team.disabled,
	);
	const selectedTeams = teams.slice(0, settings.numCountries);
	return Promise.all(
		selectedTeams.map(async (team, id) => {
			const players = await idb.cache.players.indexGetAll(
				"playersByTid",
				team.tid,
			);
			const eligiblePlayers = players.filter(
				(player) =>
					settings.includeInjured || player.injury.gamesRemaining === 0,
			);
			const roster = selectRoster(
				rankPlayers(eligiblePlayers, settings),
				settings,
			);
			return {
				id,
				name: `${team.region} ${team.name}`,
				players: roster.map(({ player }) => playerToInfo(player)),
			};
		}),
	);
};

export const preview = async (input: CustomTournamentInput) => {
	const teams =
		input.type === "allStar"
			? await buildAllStarTeams(input.selectionSettings)
			: input.selectionSettings.teamSource === "leagueTeams"
				? await buildLeagueTeams(input.selectionSettings)
				: await buildOlympicTeams(input.selectionSettings);
	return teams;
};

const createRoundGames = (
	tournament: CustomTournament,
	round: number,
	teamIDs: number[],
) => {
	const games: CustomTournamentGame[] = [];
	for (let i = 0; i < teamIDs.length; i += 2) {
		const matchupId = tournament.games.length + games.length;
		for (
			let gameNumber = 0;
			gameNumber < tournament.selectionSettings.gamesPerMatchup;
			gameNumber++
		) {
			games.push({
				id: tournament.games.length + games.length,
				round,
				homeTeam: teamIDs[i]!,
				awayTeam: teamIDs[i + 1]!,
				stage: "knockout",
				matchupId,
			});
		}
	}
	return games;
};

const createGroupGames = (tournament: CustomTournament, teamIDs: number[]) => {
	const games: CustomTournamentGame[] = [];
	const groupSize = 4;
	for (let group = 0; group < teamIDs.length / groupSize; group++) {
		const groupTeams = teamIDs.slice(
			group * groupSize,
			(group + 1) * groupSize,
		);
		for (let i = 0; i < groupTeams.length; i++) {
			for (let j = i + 1; j < groupTeams.length; j++) {
				const matchupId = tournament.games.length + games.length;
				for (
					let gameNumber = 0;
					gameNumber < tournament.selectionSettings.gamesPerMatchup;
					gameNumber++
				) {
					games.push({
						id: tournament.games.length + games.length,
						round: 0,
						homeTeam: groupTeams[i]!,
						awayTeam: groupTeams[j]!,
						stage: "group",
						group,
						matchupId,
					});
				}
			}
		}
	}
	return games;
};

const createRoundRobinGames = (
	tournament: CustomTournament,
	teamIDs: number[],
) => {
	const games: CustomTournamentGame[] = [];
	for (let i = 0; i < teamIDs.length; i++) {
		for (let j = i + 1; j < teamIDs.length; j++) {
			const matchupId = tournament.games.length + games.length;
			for (
				let gameNumber = 0;
				gameNumber < tournament.selectionSettings.gamesPerMatchup;
				gameNumber++
			) {
				games.push({
					id: tournament.games.length + games.length,
					round: 0,
					homeTeam: teamIDs[i]!,
					awayTeam: teamIDs[j]!,
					stage: "roundRobin",
					matchupId,
				});
			}
		}
	}
	return games;
};

const addRoundToSchedule = async (games: CustomTournamentGame[]) => {
	const schedule = await idb.cache.schedule.getAll();
	const day = Math.max(0, ...schedule.map((game) => game.day)) + 1;
	await idb.cache.schedule.addAll(
		games.map((game, index) => ({
			homeTid: customTid(game.homeTeam),
			awayTid: customTid(game.awayTeam),
			day: day + index,
			customTournamentGameId: game.id,
		})),
	);
};

const addInitialGamesToSchedule = async (
	games: CustomTournamentGame[],
	startDay?: number,
) => {
	const schedule = await idb.cache.schedule.getAll();
	const day = startDay ?? Math.max(0, ...schedule.map((game) => game.day)) + 1;
	const shiftedSchedule =
		startDay === undefined
			? schedule
			: schedule.map((game) => ({
					...game,
					day: game.day >= day ? game.day + games.length : game.day,
				}));
	const customSchedule = games.map((game, index) => ({
		homeTid: customTid(game.homeTeam),
		awayTid: customTid(game.awayTeam),
		day: day + index,
		customTournamentGameId: game.id,
	}));
	const combined = (
		[...customSchedule, ...shiftedSchedule] as (ScheduleGameWithoutKey & {
			gid?: number;
		})[]
	)
		.sort((a, b) => a.day - b.day)
		.map(({ gid: _gid, ...game }) => game);
	await idb.cache.schedule.clear();
	await idb.cache.schedule.addAll(combined);
};

export const create = async (input: CustomTournamentInput) => {
	if (g.get("phase") >= 4) {
		throw new Error("Custom events must be created before the draft lottery");
	}
	if (
		input.selectionSettings.format === "groups" &&
		input.selectionSettings.numCountries % 4 !== 0
	) {
		throw new Error("Olympics group play requires a multiple of 4 countries");
	}
	if (
		input.type !== "olympics" ||
		input.selectionSettings.format === "knockout"
	) {
		const numTeams =
			input.type === "allStar" ? 2 : input.selectionSettings.numCountries;
		if ((numTeams & (numTeams - 1)) !== 0) {
			throw new Error("Knockout tournaments require 2, 4, 8, or 16 teams");
		}
	}

	const settings: CustomTournamentSelectionSettings = {
		...input.selectionSettings,
		format: input.selectionSettings.format ?? "groups",
		teamSource: input.selectionSettings.teamSource ?? "countries",
		gamesPerMatchup: Math.max(
			1,
			Math.min(7, input.selectionSettings.gamesPerMatchup ?? 1),
		),
		timing: input.selectionSettings.timing ?? "beforeDraft",
		balanceMode: input.selectionSettings.balanceMode ?? "none",
		balancePercent: input.selectionSettings.balancePercent ?? 10,
		selectionFormula:
			input.selectionSettings.selectionFormula.trim() || DEFAULT_FORMULA,
	};
	const teams = await preview({ ...input, selectionSettings: settings });
	const tournament: CustomTournament = {
		season: g.get("season"),
		startingSeason: g.get("season"),
		name:
			input.name.trim() ||
			(input.type === "allStar" ? "Custom All-Star Game" : "Olympics"),
		type: input.type,
		status: "setup",
		teams,
		games: [],
		selectionSettings: settings,
		recurrence: input.recurrence,
		recurrenceInterval:
			input.recurrence === "everyNSeasons"
				? Math.max(1, input.recurrenceInterval ?? 2)
				: undefined,
	};
	await save(tournament);
	await toUI("realtimeUpdate", [["gameAttributes"]]);
	return tournament;
};

export const prepareForSeason = async () => {
	const tournament = await getAttribute();
	if (!tournament || tournament.recurrence === "once") {
		return;
	}
	const seasonOffset = g.get("season") - tournament.startingSeason;
	const interval =
		tournament.recurrence === "everySeason"
			? 1
			: Math.max(1, tournament.recurrenceInterval ?? 2);
	if (seasonOffset < 0 || seasonOffset % interval !== 0) {
		return;
	}
	tournament.teams = await preview({
		name: tournament.name,
		type: tournament.type,
		selectionSettings: tournament.selectionSettings,
		recurrence: tournament.recurrence,
		recurrenceInterval: tournament.recurrenceInterval,
	});
	tournament.season = g.get("season");
	tournament.status = "setup";
	tournament.games = [];
	tournament.championTeam = undefined;
	await save(tournament);
};

export const getCountries = async () => {
	const players = await getActivePlayers();
	const counts = new Map<string, number>();
	for (const player of players) {
		const country = helpers.getCountry(player.born.loc);
		if (country === "None") {
			continue;
		}
		counts.set(country, (counts.get(country) ?? 0) + 1);
	}
	return [...counts.entries()]
		.sort((a, b) => b[1] - a[1])
		.map(([country, count]) => ({ country, count }));
};

export const start = async (
	conditions: Conditions,
	{
		trigger = "beforeDraft",
		force = false,
	}: { trigger?: TournamentTrigger; force?: boolean } = {},
) => {
	const tournament = await getAttribute();
	if (
		!tournament ||
		tournament.season !== g.get("season") ||
		tournament.status !== "setup"
	) {
		return false;
	}
	const timing = tournament.selectionSettings.timing ?? "beforeDraft";
	const due =
		force ||
		((timing === trigger ||
			(timing === "regularSeasonDay" && trigger === "afterPreseason")) &&
			(timing !== "betweenPlayoffRounds" || g.get("phase") === 3));
	if (!due) {
		return false;
	}

	const games =
		tournament.selectionSettings.format === "groups"
			? createGroupGames(
					tournament,
					tournament.teams.map((team) => team.id),
				)
			: tournament.selectionSettings.format === "roundRobin"
				? createRoundRobinGames(
						tournament,
						tournament.teams.map((team) => team.id),
					)
				: createRoundGames(
						tournament,
						0,
						tournament.teams.map((team) => team.id),
					);
	tournament.games.push(...games);
	tournament.status = "inProgress";
	await save(tournament);
	if (trigger === "afterPreseason") {
		await addInitialGamesToSchedule(
			games,
			timing === "regularSeasonDay"
				? Math.max(0, tournament.selectionSettings.timingDay ?? 1)
				: 0,
		);
	} else {
		await addRoundToSchedule(games);
	}
	logEvent(
		{
			type: "info",
			text: `The ${tournament.name} begins before the draft.`,
			showNotification: true,
			saveToDb: true,
			tids: [],
			score: 20,
		},
		conditions,
	);
	return true;
};

export const getSimulationFactors = async (teams: Record<number, any>) => {
	const tournament = await getAttribute();
	if (
		!tournament ||
		tournament.selectionSettings.balanceMode !== "light" ||
		tournament.selectionSettings.balancePercent <= 0
	) {
		return undefined;
	}
	const customTeams = Object.values(teams).filter((team) =>
		isCustomTid(team.id),
	);
	if (customTeams.length < 2) {
		return undefined;
	}
	const average =
		customTeams.reduce((sum, team) => sum + team.ovr, 0) / customTeams.length;
	const compression = 1 - tournament.selectionSettings.balancePercent / 100;
	return Object.fromEntries(
		customTeams.map((team) => [
			team.id,
			1 + compression * (team.ovr / average - 1),
		]),
	);
};

export const recordGame = async (results: any) => {
	const tournament = await getAttribute();
	if (!tournament || tournament.status !== "inProgress") {
		return;
	}

	const teamIDs = results.team.map((team: { id: number }) =>
		customTeamID(team.id),
	);
	if (teamIDs.some((teamID: number | undefined) => teamID === undefined)) {
		return;
	}

	const game = tournament.games.find(
		(game) =>
			game.winnerTeam === undefined &&
			teamIDs.includes(game.homeTeam) &&
			teamIDs.includes(game.awayTeam),
	);
	if (!game) {
		return;
	}

	const winner = getWinner(results.team.map((team: any) => team.stat));
	game.winnerTeam = teamIDs[winner === 1 ? 1 : 0]!;

	const stage = game.stage ?? "knockout";
	const stageGames =
		stage === "group"
			? tournament.games.filter(
					(item) => item.stage === "group" && item.group === game.group,
				)
			: stage === "roundRobin"
				? tournament.games.filter((item) => item.stage === "roundRobin")
				: tournament.games.filter(
						(item) =>
							(item.stage ?? "knockout") === "knockout" &&
							item.round === game.round,
					);
	const matchupGroups =
		stage === "knockout"
			? Map.groupBy(stageGames, (item) => item.matchupId ?? item.id)
			: undefined;
	const completedMatchups = matchupGroups
		? [...matchupGroups.values()].filter((games) =>
				games.every((item) => item.winnerTeam !== undefined),
			)
		: undefined;
	const stageComplete = matchupGroups
		? completedMatchups!.length === matchupGroups.size
		: stageGames.every((item) => item.winnerTeam !== undefined);
	if (stageComplete) {
		const winners = matchupGroups
			? completedMatchups!.map((games) => {
					const wins = new Map<number, number>();
					for (const game of games) {
						wins.set(game.winnerTeam!, (wins.get(game.winnerTeam!) ?? 0) + 1);
					}
					return [...wins.entries()].sort((a, b) => b[1] - a[1])[0]![0];
				})
			: stageGames.map((item) => item.winnerTeam!);
		if (
			stage === "group" &&
			tournament.games
				.filter((item) => item.stage === "group")
				.every((item) => item.winnerTeam !== undefined)
		) {
			const groupCount = tournament.teams.length / 4;
			const qualified: number[] = [];
			for (let group = 0; group < groupCount; group++) {
				const groupTeams = tournament.teams
					.slice(group * 4, (group + 1) * 4)
					.map((team) => team.id);
				const wins = new Map(groupTeams.map((teamID) => [teamID, 0]));
				for (const groupGame of tournament.games.filter(
					(item) => item.stage === "group" && item.group === group,
				)) {
					wins.set(
						groupGame.winnerTeam!,
						(wins.get(groupGame.winnerTeam!) ?? 0) + 1,
					);
				}
				qualified.push(
					...groupTeams
						.sort((a, b) => (wins.get(b) ?? 0) - (wins.get(a) ?? 0))
						.slice(0, 2),
				);
			}
			const nextRound = createRoundGames(tournament, 0, qualified);
			tournament.games.push(...nextRound);
			await addRoundToSchedule(nextRound);
		} else if (stage === "roundRobin") {
			const wins = new Map<number, number>();
			for (const roundRobinGame of stageGames) {
				wins.set(
					roundRobinGame.winnerTeam!,
					(wins.get(roundRobinGame.winnerTeam!) ?? 0) + 1,
				);
			}
			const champion = [...wins.entries()].sort((a, b) => b[1] - a[1])[0];
			if (champion) {
				tournament.championTeam = champion[0];
				tournament.status = "complete";
			}
		} else if (stage === "knockout" && winners.length === 1) {
			tournament.championTeam = winners[0];
			tournament.status = "complete";
		} else if (stage === "knockout") {
			const nextRound = createRoundGames(tournament, game.round + 1, winners);
			tournament.games.push(...nextRound);
			await addRoundToSchedule(nextRound);
		}
	}

	await save(tournament);
};

export const hasGamesScheduled = async () => {
	const schedule = await idb.cache.schedule.getAll();
	return schedule.some((game) => game.customTournamentGameId !== undefined);
};
